import { Button } from '@/global/components/Button'
import Menu from '@/global/components/MenuV2'
import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@/global/components/ui/command'
import { cn } from '@/lib/utils'
import { insensitive } from '@acme/common'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu as MuiMenu,
    Typography,
} from '@mui/material'
import { Link as GlobalLink } from 'global/components/Link'
import {
    ChevronDownIcon,
    ExternalLinkIcon,
    MenuIcon,
    SearchIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom'
import type { Menu as TMenu } from 'routes/types'

interface IProps {
    itens: TMenu
}

function CommandSearch({ itens }: { itens: TMenu }) {
    const [open, setOpen] = useState(false)
    const navigate = useNavigate()

    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                setOpen(prev => !prev)
            }
        }
        document.addEventListener('keydown', down)
        return () => document.removeEventListener('keydown', down)
    }, [])

    const handleSelect = (redirectTo: string, newTab?: boolean) => {
        setOpen(false)
        if (newTab) {
            window.open(redirectTo, '_blank', 'noopener,noreferrer')
        } else {
            navigate(redirectTo)
        }
    }

    return (
        <>
            <Button
                variant="text"
                onClick={() => setOpen(true)}
                className={cn(
                    'ml-auto gap-2 px-2',
                    'dark:text-foreground text-background',
                    'dark:hover:bg-primary/10 hover:bg-accent/10',
                )}
                title="Buscar (Ctrl+K)"
            >
                <SearchIcon size={16} />
                <span>Buscar</span>
                <kbd className="hidden xl:inline pointer-events-none text-xs opacity-70 border rounded px-1">
                    {navigator.platform.includes('Mac') ? '⌘K' : 'Ctrl+K'}
                </kbd>
            </Button>

            <CommandDialog open={open} onOpenChange={setOpen}>
                <CommandInput placeholder="Buscar página..." />
                <CommandList>
                    <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
                    {itens?.map((item, index) => (
                        <>
                            <CommandGroup key={item.label} heading={item.label}>
                                {item.subitens.map(subitem => (
                                    <CommandItem
                                        key={subitem.redirectTo}
                                        value={[
                                            insensitive(item.label),
                                            insensitive(subitem.label),
                                            item.label,
                                            subitem.label,
                                        ].join(' ')}
                                        onSelect={() =>
                                            handleSelect(
                                                subitem.redirectTo,
                                                subitem.newTab,
                                            )
                                        }
                                        className="flex items-center gap-2"
                                    >
                                        {subitem.icon && (
                                            <span className="size-4">
                                                {subitem.icon}
                                            </span>
                                        )}
                                        <span>{subitem.label}</span>
                                        {subitem.newTab && (
                                            <ExternalLinkIcon className="ml-auto opacity-40 size-3" />
                                        )}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                            {index < itens.length - 1 && (
                                <CommandSeparator key={`sep-${item.label}`} />
                            )}
                        </>
                    ))}
                </CommandList>
            </CommandDialog>
        </>
    )
}

export function MenuResponsivo({ itens }: IProps) {
    const [menuAberto, setMenuAberto] = useState('')
    const [menuMobileAberto, setMenuMobileAberto] = useState('')
    const [isMenuVerticalAberto, setIsMenuVerticalAberto] = useState(false)

    const fecharDrawer = () => {
        setMenuMobileAberto('')
        setIsMenuVerticalAberto(false)
    }

    return [
        <div key="menu-desktop" className="hidden lg:flex w-full items-center">
            {itens?.map(item => (
                <Box key={item.label}>
                    <Button
                        variant="text"
                        className="dark:text-foreground text-background"
                        onClick={() => {
                            setMenuAberto(item.label)
                        }}
                        id={'botao_' + item.label}
                    >
                        {item.label}
                    </Button>

                    <MuiMenu
                        open={menuAberto === item.label}
                        onClose={() => setMenuAberto('')}
                        anchorReference="anchorPosition"
                        anchorPosition={{
                            top:
                                (document
                                    .getElementById('botao_' + item.label)
                                    ?.getBoundingClientRect().top ?? 0) + 40,
                            left:
                                document
                                    .getElementById('botao_' + item.label)
                                    ?.getBoundingClientRect().left ?? 0,
                        }}
                        sx={{
                            '& .MuiPaper-root': {
                                minWidth: document
                                    .getElementById('botao_' + item.label)
                                    ?.getBoundingClientRect().width,
                            },
                        }}
                    >
                        {item.subitens.map(subitem => (
                            <LinkButton
                                key={subitem.label}
                                {...subitem}
                                onClick={() => setMenuAberto('')}
                            />
                        ))}
                    </MuiMenu>
                </Box>
            ))}
            <CommandSearch itens={itens} />
        </div>,
        <div key="menu-mobile" className="flex lg:hidden w-full">
            <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{ m: 2 }}
                onClick={() => setIsMenuVerticalAberto(true)}
                id="botao_menuVertical"
            >
                <MenuIcon />
            </IconButton>
            <Drawer
                anchor="left"
                open={isMenuVerticalAberto}
                onClose={() => fecharDrawer()}
                PaperProps={{
                    style: {
                        width: 'clamp(300px, 25%, 80vw)',
                        scrollbarGutter: 'stable',
                        height: '100vh',
                        overflowY: 'auto',
                    },
                }}
            >
                <List>
                    <ListItemButton onClick={() => fecharDrawer()}>
                        <ListItemIcon>
                            <MenuIcon />
                        </ListItemIcon>
                        <ListItemText primary="Menu" />
                    </ListItemButton>
                </List>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        mt: 2,
                    }}
                >
                    {itens?.map(item => (
                        <Accordion
                            sx={{
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                '&:before': {
                                    display: 'none',
                                },
                                '&.Mui-expanded': {
                                    margin: 0,
                                    padding: 0,
                                },
                                '&.MuiPaper-root': {
                                    minHeight: 'fit-content',
                                },
                            }}
                            expanded={menuMobileAberto === item.label}
                            onClick={() => {
                                setMenuMobileAberto(
                                    menuMobileAberto === item.label
                                        ? ''
                                        : item.label,
                                )
                            }}
                            key={item.label}
                            id={'botao_' + item.label + '_mobile'}
                        >
                            <AccordionSummary expandIcon={<ChevronDownIcon />}>
                                <Typography>{item.label}</Typography>
                            </AccordionSummary>
                            <AccordionDetails
                                sx={{ flexDirection: 'column', padding: 0 }}
                            >
                                {item.subitens.map(value => (
                                    <LinkButton
                                        key={value.label}
                                        {...value}
                                        onClick={() => fecharDrawer()}
                                        mobile
                                    />
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </Drawer>
        </div>,
    ]
}

function LinkButton({
    onClick,
    mobile,
    ...props
}: TMenu[0]['subitens'][0] & {
    onClick?: () => any
    mobile?: boolean
}) {
    if (mobile)
        return (
            <ListItemButton
                sx={{ p: 0 }}
                id={'sub-botao_' + props.label + (mobile ? '_mobile' : '')}
            >
                <GlobalLink
                    onClick={onClick}
                    href={props.redirectTo}
                    sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'start',
                        alignItems: 'center',
                        p: 1.5,
                        textDecoration: 'none',
                        color: 'text.primary',
                    }}
                    target={props.newTab ? '_blank' : undefined}
                >
                    <ListItemIcon>{props.icon}</ListItemIcon>
                    <Typography>{props.label}</Typography>
                    {props.newTab && (
                        <div
                            title="Abre em uma nova aba"
                            className="ml-4 flex items-center"
                        >
                            <ExternalLinkIcon className="opacity-50 size-4" />
                        </div>
                    )}
                </GlobalLink>
            </ListItemButton>
        )

    return (
        <ReactRouterLink
            id={'sub-botao_' + props.label + (mobile ? '_mobile' : '')}
            to={props.redirectTo}
            target={props.newTab ? '_blank' : undefined}
            rel={props.newTab ? 'noopener noreferrer' : undefined}
        >
            <Menu.Item onClick={onClick}>
                <Typography
                    color="text.primary"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    {props.label}
                    {props.newTab && (
                        <div
                            title="Abre em uma nova aba"
                            className="ml-4 flex items-center"
                        >
                            <ExternalLinkIcon className="opacity-50 size-4" />
                        </div>
                    )}
                </Typography>
            </Menu.Item>
        </ReactRouterLink>
    )
}
